import { FC, Suspense, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useIdleTimer } from 'react-idle-timer';

import {
	ContestDetails,
	CreateContest,
	InviteFriendCompetition,
	Lobby,
	Profile,
	TopUpAccount,
	Verification,
	Wallet,
	EmailVerification,
	SignUp,
	Login,
	ResetPassword,
	PasswordRecovery,
	PageNotFound,
	RequestFailError
} from 'hoc/asyncComponents';
import Layout from 'hoc/layout/layout';
import { handleAuthTrack, isWhiteLabel } from 'shared/util/utility';
import Spinner from 'shared/components/spinner/spinner';
import InstallationPrompt from 'shared/components/InstallationPrompt/InstallationPrompt';
import { IState } from 'shared/interface/state';
import { useQuery } from 'shared/hooks/useQuery';
import { refreshLanguage } from 'shared/util/localization';

const App: FC = () => {
	// Using `useRef` to store a reference to the interval
	const intervalId: any = useRef();
	const navigate = useNavigate();
	const query = useQuery();
	const location = useLocation();

	const isLogin: boolean = useSelector((state: IState) => state.auth.isLogin);
	const language = (query.get('language') ? query.get('language') : localStorage.getItem('lang')) || 'de';

	const onIdle = () => {
		// Clean up the interval on component unmount
		clearInterval(intervalId.current);
		intervalId.current = undefined;
	};

	const onAction = () => {
		if (intervalId.current === undefined && isLogin) {
			handleAuthTrack();

			intervalId.current = setInterval(() => {
				handleAuthTrack();
			}, 180000);
		}
	};

	useIdleTimer({
		onIdle,
		onAction,
		onActive: () => onAction(),
		timeout: 300000,
		throttle: 300000
	});

	useEffect(() => {
		if (localStorage.getItem('lang') !== language) {
			localStorage.setItem('lang', language);
			refreshLanguage(language);
		}
	}, [language]);

	useEffect(() => {
		if (!isLogin) {
			// Clean up the interval on component unmount
			clearInterval(intervalId.current);
		}
		intervalId.current = isLogin ? undefined : null;

		if (isLogin && location.pathname.includes('/arena')) {
			navigate('/');
		}

		const onStorageUpdate = (event: Record<string, any>) => {
			if (event.key === 'authData') {
				window.location.reload();
			}
		};
		window.addEventListener('storage', onStorageUpdate);

		return () => {
			window.removeEventListener('storage', onStorageUpdate);
		};
	}, [isLogin]);

	useEffect(() => {
		if (!isWhiteLabel) return;

		// Apply the class to the body to activate white label fonts
		document.body.classList.add('white-label-mode');

		// Cleanup function to remove the class on unmount
		return () => {
			document.body.classList.remove('white-label-mode');
		};
	}, []);

	const defaultRedirection = useMemo(
		() =>
			!location.state || location.state?.id === ''
				? '/'
				: `/contest-details/${location.state.id}?contestType=Upcoming`,
		[location.state]
	);

	return (
		<>
			<InstallationPrompt />
			{isLogin ? (
				<Layout>
					<Suspense fallback={<Spinner />}>
						<Routes>
							<Route path='/' element={<Lobby />} />
							<Route path='/create-contest' element={<CreateContest />} />
							<Route path='/edit-profile' element={<Profile />} />
							<Route path='/edit-profile/verification' element={<Verification />} />
							<Route path='/contest-details/:contestId' element={<ContestDetails />} />
							<Route path='/contest/invitation/:contestId' element={<InviteFriendCompetition />} />
							<Route path='/wallet' element={<Wallet />} />
							<Route path='/wallet/top-up' element={<TopUpAccount />} />
							<Route path='/404' element={<PageNotFound />} />
							<Route path='/503' element={<RequestFailError />} />
							<Route path='/login' element={<Navigate to={defaultRedirection} />} />
							<Route path='/sign-up' element={<Navigate to='/' />} />
							<Route path='/register/refer/:code' element={<Navigate to='/' />} />
							<Route path='*' element={<Navigate to='/404' />} />
						</Routes>
					</Suspense>
				</Layout>
			) : (
				<Suspense fallback={<Spinner />}>
					<Routes>
						<Route
							path='/arena'
							element={
								<Layout>
									<Lobby />
								</Layout>
							}
						/>
						<Route
							path='/contest-details/:contestId'
							element={
								<Layout>
									<ContestDetails />
								</Layout>
							}
						/>
						<Route path='/login' element={<Login />} />
						<Route path='/sign-up' element={<SignUp />} />
						<Route path='/password-recovery' element={<PasswordRecovery />} />
						<Route path='/account/reset' element={<ResetPassword />} />
						<Route path='/account/verify' element={<EmailVerification />} />
						<Route path='/contest/invitation/:contestId' element={<InviteFriendCompetition />} />
						<Route path='/register/refer/:code' element={<SignUp />} />
						<Route path='*' element={<Navigate replace to='/login' />} />
					</Routes>
				</Suspense>
			)}
		</>
	);
};

export default App;
